
export interface LocationCoordinates {
  lat: number;
  lng: number;
}

import { GEOLOCATION_OPTIONS } from '@/config/constants';

// Helper function to round coordinates to 6 decimal places (approximately 0.1 meter precision)
export const roundCoordinates = (lat: number, lng: number): LocationCoordinates => {
  return {
    lat: Math.round(lat * 1000000) / 1000000,
    lng: Math.round(lng * 1000000) / 1000000
  };
};

/**
 * Convert meters to feet
 */
export const metersToFeet = (meters: number): number => {
  return meters * 3.28084;
};

/**
 * Check if two coordinates are nearby within a specified threshold
 */
export const areCoordinatesNearby = (
  coord1: { lat: number; lng: number },
  coord2: { lat: number; lng: number },
  thresholdMeters: number = 10
): boolean => {
  const distance = calculateDistance(coord1, coord2);
  const distanceInFeet = metersToFeet(distance);
  
  console.log('Distance calculation:', {
    distanceMeters: distance.toFixed(2),
    distanceFeet: distanceInFeet.toFixed(1),
    thresholdMeters,
    thresholdFeet: metersToFeet(thresholdMeters).toFixed(1),
    isNearby: distance <= thresholdMeters
  });
  
  return distance <= thresholdMeters;
};

/**
 * Calculate distance between two coordinates using Haversine formula
 */
export const calculateDistance = (
  coord1: { lat: number; lng: number },
  coord2: { lat: number; lng: number }
): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = coord1.lat * Math.PI / 180;
  const φ2 = coord2.lat * Math.PI / 180;
  const Δφ = (coord2.lat - coord1.lat) * Math.PI / 180;
  const Δλ = (coord2.lng - coord1.lng) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // Distance in meters
};

export const getCurrentLocation = (options?: PositionOptions): Promise<LocationCoordinates> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    const defaultOptions: PositionOptions = GEOLOCATION_OPTIONS;

    // Use provided options or fall back to defaults
    const geolocationOptions = options || defaultOptions;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const rawLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        // Round coordinates for consistency and Google Maps compatibility
        const location = roundCoordinates(rawLocation.lat, rawLocation.lng);
        console.log('Raw location:', rawLocation);
        console.log('Rounded location for Google Maps compatibility:', location);
        resolve(location);
      },
      (error) => {
        let errorMessage = 'Unable to get your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
        reject(new Error(errorMessage));
      },
      geolocationOptions
    );
  });
};
